import React from 'react';
import { X, AlertCircle } from 'lucide-react';

/**
 * ErrorModal Component
 * Displays validation errors or other error messages in a modal dialog
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {string} props.title - Error title (default: "Validation Error")
 * @param {string} props.message - Error message to display
 * @param {string} props.className - Additional CSS classes
 */
const ErrorModal = ({
  isOpen,
  onClose,
  title = "Validation Error",
  message,
  className = ''
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={`bg-white rounded-lg max-w-md w-full mx-auto shadow-xl ${className}`}>
        {/* Header */}
        <div className="bg-red-50 p-4 rounded-t-lg border-b border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-red-100 p-1.5 rounded-full">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-red-900">
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-800 leading-relaxed">
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
